import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import { configAPI } from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Settings, Power, Trophy, Target, AlertCircle, Music, Smartphone, MessageCircle, Save, Bell, Bot } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import PaymentMethodsConfig from './PaymentMethodsConfig';

const SystemConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Estado local para o link do suporte
  const [supportLink, setSupportLink] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await configAPI.get();
      setConfig(response.data);
      setSupportLink(response.data?.support_button_link || '');
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (field, value) => {
    setUpdating(true);
    try {
      const updateData = { [field]: value };
      const response = await configAPI.update(updateData);
      setConfig(response.data);
      toast.success('Configuração atualizada!');
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
      loadConfig(); // Recarrega para reverter
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveSupportLink = async () => {
    setSavingLink(true);
    try {
      const response = await configAPI.update({ support_button_link: supportLink });
      setConfig(response.data);
      toast.success('Link de suporte atualizado!');
    } catch (error) {
      toast.error('Erro ao atualizar link');
    } finally {
      setSavingLink(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-config">
      <div>
        <h2 className="text-2xl font-bold">Configurações do Sistema</h2>
        <p className="text-gray-600">Gerencie as configurações globais da plataforma</p>
      </div>

      {config?.sales_paused && (
        <Alert variant="destructive" data-testid="sales-paused-alert">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>ATENÇÃO:</strong> As vendas estão pausadas! Os clientes não podem fazer novos pedidos.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Vendas */}
        <Card data-testid="sales-config-card" className={config?.sales_paused ? 'border-red-300' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Power className="h-5 w-5" />
              Controle de Vendas
            </CardTitle>
            <CardDescription>
              Pause temporariamente todas as vendas do sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="sales-paused" className="text-base">
                  Status das Vendas
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.sales_paused ? 'Vendas pausadas' : 'Vendas ativas'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.sales_paused ? 'destructive' : 'default'}>
                  {config?.sales_paused ? 'PAUSADO' : 'ATIVO'}
                </Badge>
                <Switch
                  data-testid="sales-paused-switch"
                  id="sales-paused"
                  checked={!config?.sales_paused}
                  onCheckedChange={(checked) => handleToggle('sales_paused', !checked)}
                  disabled={updating}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* NOVO - Verificação de Saldo */}
        <Card className="border-blue-300">
          <CardHeader>
            <CardTitle>💰 Verificação de Saldo KAKO</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 border rounded bg-blue-50">
              <div>
                <p className="font-semibold">Verificar saldo antes de vender:</p>
                <p className="text-sm text-gray-600 mt-1">
                  Quando ativo, pacotes ficam indisponíveis se saldo KAKO for insuficiente
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={config?.check_kako_balance ?? true}
                  onCheckedChange={(checked) => handleToggle('check_kako_balance', checked)}
                />
                <Badge variant={config?.check_kako_balance ? 'default' : 'secondary'}>
                  {config?.check_kako_balance ? 'ATIVO' : 'INATIVO'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>


        {/* Ranking */}
        <Card data-testid="ranking-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Ranking Diário
            </CardTitle>
            <CardDescription>
              Ative ou desative a competição diária
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="ranking-active" className="text-base">
                  Competição Diária
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.ranking_active ? 'Ranking ativo' : 'Ranking desativado'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.ranking_active ? 'default' : 'secondary'}>
                  {config?.ranking_active ? 'ATIVO' : 'INATIVO'}
                </Badge>
                <Switch
                  data-testid="ranking-active-switch"
                  id="ranking-active"
                  checked={config?.ranking_active}
                  onCheckedChange={(checked) => handleToggle('ranking_active', checked)}
                  disabled={updating}
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="ranking-show-prizes" className="text-base">
                    Exibir Prêmios e Histórico
                  </Label>
                  <p className="text-sm text-gray-600">
                    Permite resgatar prêmios mesmo com ranking desativado
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={config?.ranking_show_prizes ? 'default' : 'secondary'}>
                    {config?.ranking_show_prizes ? 'SIM' : 'NÃO'}
                  </Badge>
                  <Switch
                    data-testid="ranking-show-prizes-switch"
                    id="ranking-show-prizes"
                    checked={config?.ranking_show_prizes}
                    onCheckedChange={(checked) => handleToggle('ranking_show_prizes', checked)}
                    disabled={updating}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tarefas */}
        <Card data-testid="tasks-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-500" />
              Tarefas Diárias
            </CardTitle>
            <CardDescription>
              Ative ou desative o sistema de tarefas com bônus
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="tasks-active" className="text-base">
                  Status das Tarefas
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.tasks_active ? 'Tarefas ativas' : 'Tarefas desativadas'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.tasks_active ? 'default' : 'secondary'}>
                  {config?.tasks_active ? 'ATIVO' : 'INATIVO'}
                </Badge>
                <Switch
                  data-testid="tasks-active-switch"
                  id="tasks-active"
                  checked={config?.tasks_active}
                  onCheckedChange={(checked) => handleToggle('tasks_active', checked)}
                  disabled={updating}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Texto Scrolling */}
        <Card data-testid="scrolling-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-500" />
              Texto Scrolling
            </CardTitle>
            <CardDescription>
              Barra de compradores recentes no topo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="scrolling-active" className="text-base">
                  Status do Scrolling
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.scrolling_text_active ? 'Texto ativo' : 'Texto desativado'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.scrolling_text_active ? 'default' : 'secondary'}>
                  {config?.scrolling_text_active ? 'ATIVO' : 'INATIVO'}
                </Badge>
                <Switch
                  data-testid="scrolling-active-switch"
                  id="scrolling-active"
                  checked={config?.scrolling_text_active}
                  onCheckedChange={(checked) => handleToggle('scrolling_text_active', checked)}
                  disabled={updating}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Playlist Musical */}
        <Card data-testid="playlist-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Music className="h-5 w-5 text-purple-500" />
              Playlist Musical
            </CardTitle>
            <CardDescription>
              Ative ou desative a reprodução automática de músicas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="playlist-active" className="text-base">
                  Status da Playlist
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.playlist_active ? 'Playlist ativa' : 'Playlist desativada'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.playlist_active ? 'default' : 'secondary'}>
                  {config?.playlist_active ? 'ATIVO' : 'INATIVO'}
                </Badge>
                <Switch
                  data-testid="playlist-active-switch"
                  id="playlist-active"
                  checked={config?.playlist_active}
                  onCheckedChange={(checked) => handleToggle('playlist_active', checked)}
                  disabled={updating}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* PWA - Notificações */}
        <Card data-testid="pwa-config-card" className="border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-orange-500" />
              PWA - Instalar App
            </CardTitle>
            <CardDescription>
              Controle o prompt de instalação do aplicativo para usuários
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="pwa-notification" className="text-base">
                  Exibir Prompt de Instalação
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.pwa_notification_enabled 
                    ? 'Usuários verão sugestão para instalar o app' 
                    : 'Prompt de instalação desativado'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.pwa_notification_enabled ? 'default' : 'secondary'}>
                  {config?.pwa_notification_enabled ? 'ATIVO' : 'INATIVO'}
                </Badge>
                <Switch
                  data-testid="pwa-notification-switch"
                  id="pwa-notification"
                  checked={config?.pwa_notification_enabled ?? true}
                  onCheckedChange={(checked) => handleToggle('pwa_notification_enabled', checked)}
                  disabled={updating}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Botão de Suporte WhatsApp */}
        <Card data-testid="support-button-config-card" className="border-green-200 md:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-500" />
              Botão de Suporte (WhatsApp)
            </CardTitle>
            <CardDescription>
              Configure o botão flutuante de suporte exibido para os clientes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Switch Ativar/Desativar */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="support-enabled" className="text-base font-medium">
                  Exibir Botão
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.support_button_enabled !== false 
                    ? 'Botão visível para todos os clientes' 
                    : 'Botão oculto'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={config?.support_button_enabled !== false ? 'default' : 'secondary'}>
                  {config?.support_button_enabled !== false ? 'VISÍVEL' : 'OCULTO'}
                </Badge>
                <Switch
                  data-testid="support-enabled-switch"
                  id="support-enabled"
                  checked={config?.support_button_enabled !== false}
                  onCheckedChange={(checked) => handleToggle('support_button_enabled', checked)}
                  disabled={updating}
                />
              </div>
            </div>

            {/* Switch Online/Offline */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="space-y-1">
                <Label htmlFor="support-online" className="text-base font-medium">
                  Status do Suporte
                </Label>
                <p className="text-sm text-gray-600">
                  {config?.support_button_online !== false 
                    ? 'Exibindo como Online (verde)' 
                    : 'Exibindo como Offline (cinza)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Badge 
                  variant={config?.support_button_online !== false ? 'default' : 'secondary'}
                  className={config?.support_button_online !== false ? 'bg-green-500' : 'bg-gray-400'}
                >
                  {config?.support_button_online !== false ? '🟢 ONLINE' : '⚫ OFFLINE'}
                </Badge>
                <Switch
                  data-testid="support-online-switch"
                  id="support-online"
                  checked={config?.support_button_online !== false}
                  onCheckedChange={(checked) => handleToggle('support_button_online', checked)}
                  disabled={updating}
                />
              </div>
            </div>

            {/* Campo de Link */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <Label htmlFor="support-link" className="text-base font-medium">
                Link de Redirecionamento
              </Label>
              <p className="text-sm text-gray-600">
                URL que será aberta ao clicar no botão (WhatsApp, Telegram, etc.)
              </p>
              <div className="flex gap-2 mt-2">
                <Input
                  id="support-link"
                  data-testid="support-link-input"
                  placeholder="https://api.whatsapp.com/send/?phone=5528999565056"
                  value={supportLink}
                  onChange={(e) => setSupportLink(e.target.value)}
                  className="flex-1"
                />
                <Button 
                  onClick={handleSaveSupportLink}
                  disabled={savingLink || supportLink === (config?.support_button_link || '')}
                  className="bg-green-500 hover:bg-green-600"
                >
                  {savingLink ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-400">
                Deixe vazio para usar o link padrão do WhatsApp
              </p>
            </div>

            {/* Preview */}
            <div className="p-3 border rounded-lg">
              <p className="text-sm font-medium mb-2">Preview:</p>
              <div className="flex items-center gap-3">
                <div className={`${
                  config?.support_button_online !== false 
                    ? 'bg-green-500' 
                    : 'bg-gray-400'
                } text-white rounded-full p-3 flex items-center gap-2`}>
                  <div className="relative">
                    <MessageCircle className="w-5 h-5" />
                    <span 
                      className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white ${
                        config?.support_button_online !== false ? 'bg-green-300' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                  <span className="text-sm font-medium">
                    {config?.support_button_online !== false ? 'Suporte' : 'Offline'}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  ← Assim aparece para o cliente
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notificação de Recarga */}
        <Card data-testid="recharge-notification-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-500" />
              Notificação de Crédito
            </CardTitle>
            <p className="text-sm text-gray-500">
              Enviar mensagem automática para o cliente quando o pedido for creditado
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full">
                  <Bell className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Enviar Confirmação via DM</p>
                  <p className="text-xs text-gray-500">
                    Cliente recebe notificação no Kako quando crédito for realizado
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge 
                  variant={config?.recharge_notification_enabled ? 'default' : 'secondary'}
                  className={config?.recharge_notification_enabled ? 'bg-blue-500' : ''}
                >
                  {config?.recharge_notification_enabled ? '✅ ATIVO' : '❌ INATIVO'}
                </Badge>
                <Switch
                  checked={config?.recharge_notification_enabled ?? false}
                  onCheckedChange={(checked) => handleToggle('recharge_notification_enabled', checked)}
                  data-testid="recharge-notification-switch"
                />
              </div>
            </div>

            {/* Exemplo de mensagem */}
            {config?.recharge_notification_enabled && (
              <div className="p-3 border rounded-lg bg-gray-50">
                <p className="text-sm font-medium mb-2 text-gray-700">📩 Exemplo de mensagem enviada:</p>
                <div className="bg-white p-3 rounded border text-sm text-gray-600 font-mono">
                  <p>Pedido #PED-2024-001</p>
                  <p className="mt-1">Quantia: 5000 moedas.</p>
                  <p className="mt-1">📅 DATA: 22/03/2026</p>
                  <p>⏰ Horário: 14:30hs</p>
                  <p className="mt-1">Status: Creditado! ✅</p>
                  <p className="mt-1">Obrigado e boa sorte! 🧡🍀</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bot de Vendas (Chatbot) */}
        <Card data-testid="sales-bot-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-orange-500" />
              Bot de Vendas
            </CardTitle>
            <p className="text-sm text-gray-500">
              Responder clientes automaticamente no chat do app e vender moedas via PIX
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-full">
                  <Bot className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Atendimento Automático via Chat</p>
                  <p className="text-xs text-gray-500">
                    Bot responde DMs, exibe pacotes e gera PIX direto no chat do Kako
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  variant={config?.chatbot_enabled !== false ? 'default' : 'secondary'}
                  className={config?.chatbot_enabled !== false ? 'bg-orange-500' : ''}
                >
                  {config?.chatbot_enabled !== false ? '✅ ATIVO' : '❌ INATIVO'}
                </Badge>
                <Switch
                  checked={config?.chatbot_enabled !== false}
                  onCheckedChange={(checked) => handleToggle('chatbot_enabled', checked)}
                  data-testid="sales-bot-switch"
                />
              </div>
            </div>

            {config?.chatbot_enabled === false && (
              <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                <p className="text-sm text-red-700">
                  ⚠️ Bot desativado: clientes não receberão respostas automáticas no chat e não poderão comprar pelo bot.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Métodos de Pagamento */}
        <PaymentMethodsConfig />


        {/* Status Geral */}
        <Card data-testid="system-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Status Geral do Sistema
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm font-medium">Sistema de Vendas</span>
                <Badge variant={!config?.sales_paused ? 'default' : 'destructive'}>
                  {!config?.sales_paused ? '🟢 Online' : '🔴 Pausado'}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm font-medium">Ranking Semanal</span>
                <Badge variant={config?.ranking_active ? 'default' : 'secondary'}>
                  {config?.ranking_active ? '🟢 Ativo' : '⚫ Inativo'}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm font-medium">Tarefas Diárias</span>
                <Badge variant={config?.tasks_active ? 'default' : 'secondary'}>
                  {config?.tasks_active ? '🟢 Ativo' : '⚫ Inativo'}
                </Badge>
              </div>
              <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm font-medium">Playlist Musical</span>
                <Badge variant={config?.playlist_active ? 'default' : 'secondary'}>
                  {config?.playlist_active ? '🟢 Ativo' : '⚫ Inativo'}
                </Badge>
              </div>
              {config?.updated_at && (
                <p className="text-xs text-gray-500 text-center mt-4">
                  Última atualização: {new Date(config.updated_at).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SystemConfig;
